import React, { ReactElement } from 'react';

import {
  useField,
  FieldProps,
  FormSpy,
  useForm,
  FieldRenderProps,
} from 'react-final-form';

import { DateInput as Input } from '@vk-hr-tek/ui/input';
import {
  CALENDAR_MIN_DATE,
  CALENDAR_MAX_DATE,
} from '@vk-hr-tek/ui/services/calendar';

import { compose, ValidateType } from '../validation';

/**
 * Компонент для ввода даты с использованием календаря.
 * @param {string} label - Подпись поля ввода.
 * @param {string} name - Имя поля ввода, используется для связывания с формой.
 * @param {string} [tooltip] - Вспомогательный текст, отображаемый при наведении на поле.
 * @param {() => void} [onClear] - Функция, вызываемая при очистке поля.
 * @param {string} [placeholder='ДД.ММ.ГГГГ'] - Текст-заполнитель для поля ввода.
 * @param {boolean} [required=false] - Флаг, указывающий, является ли поле обязательным.
 * @param {boolean} [disablePast=false] - Флаг, запрещающий выбор прошлых дат.
 * @param {Date} [minDate=Calendar.minDate] - Минимальная дата, доступная для выбора.
 * @param {Date} [maxDate=Calendar.maxDate] - Максимальная дата, доступная для выбора.
 * @param {string} [recognizedValue] - Распознанное значение даты.
 * @param {(date: Date) => boolean} [shouldDisableDate] - Функция для определения, должна ли быть недоступна для выбора конкретная дата.
 * @param {string} [externalError] - Внешняя ошибка, отображаемая в поле.
 * @param {string} [otherSelectedDate] - Другая выбранная дата, может использоваться для валидации диапазона дат.
 * @param {boolean} [clearable=false] - Флаг, позволяющий очищать поле ввода.
 * @param {boolean} [disabled=false] - Флаг, отключающий поле ввода.
 * @param {boolean} [showRange=false] - Флаг, указывающий, должен ли отображаться диапазон дат.
 * @param {boolean} [alwaysShowClear=false] - Флаг, всегда отображающий кнопку очистки.
 * @param {boolean} [loading=false] - Флаг, указывающий, что поле находится в состоянии загрузки.
 * @param {boolean} [isRecognitionUsedBefore=false] - Флаг, указывающий, использовалась ли ранее распознавание значений.
 * @param {string} [testId] - Идентификатор для тестирования.
 * @param {ValidateType[]} [validateArray] - Массив валидаторов для поля.
 * @param {ValidateType[]} [warnings] - Массив валидаторов для предупреждений.
 * @param {(value: string | undefined) => React.ReactNode} [after] - Функция, возвращающая дополнительный контент после поля.
 * @param {(day: Date | null) => ReactElement | null} [renderDayTooltip] - функция, которая рендерит тултип для дня в каледаре.
 * @param {(day: Date | null, isInRange: boolean) => ReactElement | null} [renderDayFooter] - функция, которая рендерит что-либо под цифрой даты дня в календаре.
 * @param {DateInputProps} rest - Дополнительные пропсы для поля.
 * @param {boolean} [validationWithoutTouch=false] - Показывать ошибку без взаимодействия с полем.
 * @param {boolean} [validationWhenDisabled=false] - Показывать ошибку если поле задизейблено.
 * @param {FieldProps<string | undefined, FieldRenderProps<string | undefined>>} rest - Дополнительные пропсы для поля.
 * @returns {React.ReactNode} Компонент DateInput.
 */
export const DateInput = ({
  label,
  name,
  tooltip,
  onClear,
  placeholder = 'ДД.ММ.ГГГГ',
  required = false,
  disablePast = false,
  minDate = CALENDAR_MIN_DATE,
  maxDate = CALENDAR_MAX_DATE,
  recognizedValue,
  shouldDisableDate,
  externalError,
  otherSelectedDate,
  clearable = false,
  disabled = false,
  showRange = false,
  alwaysShowClear = false,
  loading = false,
  isRecognitionUsedBefore = false,
  testId,
  validateArray,
  warnings,
  renderDayTooltip,
  renderDayFooter,
  validationWithoutTouch = false,
  validationWhenDisabled = false,
  ...rest
}: { after?: (value: string | undefined) => React.ReactNode } & FieldProps<
  string | undefined,
  FieldRenderProps<string | undefined>
> & {
    validateArray?: (ValidateType | undefined)[];
    warnings?: (ValidateType | undefined)[];
    validationWithoutTouch?: boolean;
    validationWhenDisabled?: boolean;
    renderDayTooltip?: (day: Date | null) => ReactElement | null;
    renderDayFooter?: (
      day: Date | null,
      isInRange: boolean,
    ) => ReactElement | null;
  }) => {
  const { mutators } = useForm();

  const form = useForm();

  const { input, meta } = useField(name, {
    validate: compose(validateArray || []),
    ...rest,
  });

  const composedWarnings = compose(warnings || []);

  /**
   * Флаг для отображения ошибки в зависимости от состояния disabled
   * @type {boolean}
   */
  const errorDisabled = disabled || !validationWhenDisabled;

  /**
   * Флаг для отображения ошибки
   * @type {boolean}
   */
  const renderError =
    !errorDisabled && validationWithoutTouch
      ? !!input.value && !!meta.error
      : meta.touched && !!meta.error;

  /**
   * Флаг для отображения ворнинга
   * @type {boolean}
   */
  const renderWarning =
    !errorDisabled && validationWithoutTouch
      ? !!input.value && !!meta.data?.warning
      : meta.touched && !!meta.data?.warning;

  return (
    <>
      <Input
        {...input}
        testId={testId}
        formBlur={form.blur} //TODO find better solution use form.blur inside input.onChange
        label={label}
        tooltip={tooltip}
        onClear={onClear}
        disabled={disabled}
        placeholder={placeholder}
        error={renderError && meta.error}
        warning={renderWarning && meta.data?.warning}
        minDate={minDate}
        maxDate={maxDate}
        recognizedValue={recognizedValue}
        isRecognitionUsedBefore={isRecognitionUsedBefore}
        required={required}
        disablePast={disablePast}
        otherSelectedDate={otherSelectedDate}
        externalError={externalError}
        shouldDisableDate={shouldDisableDate}
        clearable={clearable}
        showRange={showRange}
        alwaysShowClear={alwaysShowClear}
        loading={loading}
        renderDayTooltip={renderDayTooltip}
        renderDayFooter={renderDayFooter}
      />
      <FormSpy
        subscription={{ values: true }}
        onChange={({ values }) => {
          if (values) {
            mutators.setFieldData(input.name, {
              warning: composedWarnings(values[input.name], values),
            });
          }
        }}
      />
    </>
  );
};
